import React from 'react';
import { Link } from 'react-router-dom';
import BuildInfo from '../components/BuildInfo';
import './LandingPage.css';

const ACCENT = '#2563eb';

const Wordmark = ({ size = 28, accent = ACCENT, ink = '#000' }) => (
  <span className="pb-wordmark" style={{ lineHeight: 1 }}>
    <span
      style={{
        fontFamily: 'JetBrains Mono, monospace',
        fontSize: size * 1.25,
        fontWeight: 500,
        color: accent,
        letterSpacing: -size * 0.04,
        lineHeight: 1,
      }}
    >
      [
    </span>
    <span
      style={{
        fontFamily: 'JetBrains Mono, monospace',
        fontSize: size,
        fontWeight: 600,
        color: ink,
        letterSpacing: -size * 0.04,
        lineHeight: 1,
      }}
    >
      paperboy
    </span>
    <span
      style={{
        fontFamily: 'JetBrains Mono, monospace',
        fontSize: size * 1.25,
        fontWeight: 500,
        color: accent,
        letterSpacing: -size * 0.04,
        lineHeight: 1,
      }}
    >
      ]
    </span>
  </span>
);

const Mark = ({ size = 32, accent = ACCENT, ink = '#000', inverted = false }) => (
  <span
    style={{
      width: size,
      height: size,
      background: inverted ? accent : ink,
      borderRadius: size * 0.18,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: 'JetBrains Mono, monospace',
      fontSize: size * 0.5,
      fontWeight: 700,
      color: inverted ? ink : accent,
      letterSpacing: -size * 0.02,
      flexShrink: 0,
    }}
  >
    [p]
  </span>
);

const MonoTag = ({ children, color = 'rgba(0,0,0,0.55)' }) => (
  <span
    style={{
      fontFamily: 'JetBrains Mono, monospace',
      fontSize: 10.5,
      letterSpacing: 1.2,
      textTransform: 'uppercase',
      color,
      fontWeight: 600,
    }}
  >
    {children}
  </span>
);

const PREVIEW_STORIES = [
  {
    id: 'openai',
    tag: 'AI',
    sources: 14,
    headline:
      'OpenAI ships agent-native browser; early reviews call it the first real Chrome challenger.',
    time: '06:11',
  },
  {
    id: 'senate',
    tag: 'Policy',
    sources: 9,
    headline: 'Senate clears AI procurement bill 67-31, sending it to the House.',
    time: '05:48',
  },
  {
    id: 'hurricane',
    tag: 'Climate',
    sources: 7,
    headline: 'Atlantic hurricane forecast revised down for the second straight week.',
    time: '05:30',
  },
];

const SAMPLE_OUTLETS = [
  'Bloomberg', 'WSJ', 'Reuters', 'FT', 'NYT', 'WaPo', 'Axios',
  'The Verge', 'Stratechery', 'Platformer', 'Information', 'Politico',
  'TechCrunch', 'Wired', 'Ars Technica', 'Economist', 'MIT Tech Review',
  'Pragmatic Capitalist', 'Matt Levine', 'Heatmap', 'Punchbowl',
  'Casey Newton', 'Garbage Day', 'Ben Thompson', 'Import AI',
  'Latent Space', 'Lex',
];

const STEPS = [
  {
    n: '01',
    t: 'You tell us what you care about',
    d: "Pick topics, follow people, tell us your verticals. Granular as you want — 'mid-cap biotech' beats 'health'.",
  },
  {
    n: '02',
    t: 'We scour 12,847 sources, every morning',
    d: 'Newspapers, blogs, newsletters, primary docs. We rank by signal — original reporting first, not aggregator chatter.',
  },
  {
    n: '03',
    t: 'You get one front page, then go',
    d: 'About twelve minutes of reading. We link you to the originals; we don’t keep you on Paperboy.',
  },
];

const PRICING = [
  { name: 'Free', price: '$0', desc: 'Two topics, daily front page, web only.', cta: 'Start free', highlight: false },
  { name: 'Reader', price: '$9', per: '/mo', desc: 'Unlimited topics, mobile app, archives, priority sources.', cta: 'Try Reader', highlight: true },
  { name: 'Wire', price: '$24', per: '/mo', desc: 'Reader + custom feeds, team sharing, RSS export, source-level controls.', cta: 'Try Wire', highlight: false },
];

const FOOTER_LINKS = ['Sources', 'Manifesto', 'Changelog', 'Press kit', 'Status', 'Contact'];

const LandingPage = () => {
  const accent = ACCENT;

  return (
    <div className="paperboy-landing">
      {/* TOP BAR */}
      <header className="pb-topbar">
        <Link to="/" className="pb-topbar-brand" aria-label="Paperboy home">
          <Wordmark size={20} accent={accent} />
        </Link>
        <div className="pb-topbar-meta">
          <MonoTag>06:42 EST</MonoTag>
          <Link to="/login" className="pb-topbar-signin">
            Sign in
          </Link>
          <Link to="/register" className="pb-btn pb-btn-primary pb-btn-sm">
            Get Paperboy
            <span className="pb-btn-dot" style={{ background: accent }} />
          </Link>
        </div>
      </header>

      {/* LIVE TICKER */}
      <div className="pb-ticker">
        <span className="pb-ticker-live" style={{ color: accent }}>
          <span className="pb-ticker-dot" style={{ background: accent }} />
          LIVE
        </span>
        <span>Scouring 12,847 sources</span>
        <span className="pb-dot-sep">·</span>
        <span>34 stories ranked this hour</span>
        <span className="pb-dot-sep">·</span>
        <span>Last update 4 min ago</span>
        <span className="pb-dot-sep">·</span>
        <span style={{ color: accent }}>FED ↗ MARKETS · OPENAI ↗ AI · SENATE ↗ POLICY</span>
      </div>

      {/* HERO */}
      <section className="pb-hero">
        <div className="pb-hero-copy">
          <div className="pb-eyebrow">
            <span className="pb-eyebrow-dot" style={{ background: accent }} />
            <MonoTag>Reader, not feed · No ads · No rewrites</MonoTag>
          </div>

          <h1 className="pb-hero-title">
            We don’t write<br />
            the news.<br />
            <span className="pb-hero-accent" style={{ color: accent }}>We scour</span> for it.
          </h1>

          <p className="pb-hero-sub">
            Paperboy reads <strong>12,847 sources</strong> every morning so you don’t have to. Tell us what you
            care about — we rank the strongest reporting and link you to the originals. No summaries, no
            algorithmic mush, no infinite scroll.
          </p>

          <div className="pb-hero-cta">
            <Link to="/register" className="pb-btn pb-btn-primary pb-btn-lg">
              Build my front page →
              <span className="pb-btn-dot" style={{ background: accent }} />
            </Link>
            <Link to="/login" className="pb-btn pb-btn-ghost pb-btn-lg">
              See a sample edition
            </Link>
          </div>

          <div className="pb-hero-meta">
            <span>★★★★★ 4.9 · 2,847 readers</span>
            <span className="pb-dot-sep">·</span>
            <span>Free for 14 days</span>
            <span className="pb-dot-sep">·</span>
            <span>Cancel any time</span>
          </div>
        </div>

        {/* Floating front-page preview */}
        <div className="pb-preview">
          <div className="pb-preview-head">
            <div className="pb-preview-id">
              <Mark size={22} accent={accent} ink="#fafafa" inverted />
              <span className="pb-preview-id-text">Front page · For Anya</span>
            </div>
            <span className="pb-preview-time" style={{ color: accent }}>● TUE 06:42</span>
          </div>

          <div className="pb-preview-lead">
            <div className="pb-preview-tag" style={{ color: accent }}>
              ▸ MARKETS · 22 SOURCES SCOURED
            </div>
            <div className="pb-preview-headline">
              The Fed signals the first cut of 2026 as inflation cools to 2.1%.
            </div>
            <div className="pb-preview-outlets">
              {['Bloomberg ↗', 'WSJ ↗', 'Reuters ↗', 'FT ↗', '+18'].map((s, i) => (
                <span
                  key={s}
                  className="pb-preview-outlet"
                  style={{
                    background: i === 4 ? accent : 'transparent',
                    color: i === 4 ? '#000' : '#fafafa',
                    border: i === 4 ? 'none' : '1px solid rgba(255,255,255,0.2)',
                  }}
                >
                  {s}
                </span>
              ))}
            </div>
          </div>

          {PREVIEW_STORIES.map((s) => (
            <div key={s.id} className="pb-preview-row">
              <div className="pb-preview-row-meta">
                <span className="pb-preview-row-tag" style={{ color: accent }}>
                  {s.tag.toUpperCase()} · {s.sources} SOURCES
                </span>
                <span className="pb-preview-row-time">{s.time}</span>
              </div>
              <div className="pb-preview-row-headline">{s.headline}</div>
            </div>
          ))}

          <div className="pb-preview-foot">+30 more stories · ~12 min read</div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="pb-block">
        <div className="pb-block-head">
          <h2 className="pb-block-title">Three things, that’s it.</h2>
          <MonoTag>HOW PAPERBOY WORKS · 03 STEPS</MonoTag>
        </div>
        <div className="pb-steps">
          {STEPS.map((s, i) => (
            <div key={s.n} className={`pb-step ${i < STEPS.length - 1 ? 'pb-step-divider' : ''}`}>
              <div className="pb-step-n-row">
                <span className="pb-step-n" style={{ color: accent }}>{s.n}</span>
                <span className="pb-step-rule" />
              </div>
              <div className="pb-step-t">{s.t}</div>
              <div className="pb-step-d">{s.d}</div>
            </div>
          ))}
        </div>
      </section>

      {/* NOT THIS / THIS */}
      <section className="pb-block">
        <div className="pb-philosophy">
          <div className="pb-philosophy-not">
            <MonoTag color="rgba(0,0,0,0.5)">NOT THIS</MonoTag>
            <div className="pb-philosophy-line">
              <s>An algorithm decides what’s “for you” based on what makes you angry.</s>
            </div>
            <div className="pb-philosophy-line">
              <s>An AI summarizes thirty articles into one bland paragraph.</s>
            </div>
            <div className="pb-philosophy-line">
              <s>A “brief” written by someone who didn’t read the source.</s>
            </div>
          </div>
          <div className="pb-philosophy-this">
            <MonoTag color={accent}>THIS</MonoTag>
            <div className="pb-philosophy-line pb-philosophy-line-light">
              You picked your topics. We tell you who covered them best today.
            </div>
            <div className="pb-philosophy-line pb-philosophy-line-light">
              We rank. We don’t write.
            </div>
            <div className="pb-philosophy-line pb-philosophy-line-light">
              You read the originals.{' '}
              <span style={{ color: accent }}>The journalists deserve the click.</span>
            </div>
          </div>
        </div>
      </section>

      {/* SOURCES */}
      <section className="pb-block">
        <div className="pb-block-head">
          <h2 className="pb-block-title">From 12,847 sources, give or take.</h2>
          <MonoTag>OUTLETS · NEWSLETTERS · PRIMARY DOCS</MonoTag>
        </div>
        <div className="pb-sources">
          {SAMPLE_OUTLETS.map((o) => (
            <span key={o} className="pb-source-chip">
              {o}
            </span>
          ))}
          <span
            className="pb-source-chip pb-source-chip-more"
            style={{ background: accent, color: '#fff', borderColor: accent }}
          >
            + 12,800 more
          </span>
        </div>
      </section>

      {/* PRICING */}
      <section className="pb-block">
        <div className="pb-pricing">
          {PRICING.map((p) => (
            <div
              key={p.name}
              className={`pb-price-card ${p.highlight ? 'pb-price-card-highlight' : ''}`}
            >
              {p.highlight && (
                <span className="pb-price-badge" style={{ background: accent }}>
                  MOST PICKED
                </span>
              )}
              <MonoTag color={p.highlight ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.5)'}>
                {p.name.toUpperCase()}
              </MonoTag>
              <div className="pb-price-amount">
                <span className="pb-price-num">{p.price}</span>
                {p.per && <span className="pb-price-per">{p.per}</span>}
              </div>
              <div className="pb-price-desc">{p.desc}</div>
              <Link
                to="/register"
                className={`pb-price-cta ${p.highlight ? 'pb-price-cta-highlight' : ''}`}
                style={p.highlight ? { background: accent } : undefined}
              >
                {p.cta} →
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* FOOTER */}
      <footer className="pb-footer">
        <div className="pb-footer-brand">
          <Wordmark size={32} accent={accent} />
          <div className="pb-footer-tag">
            Reader, not feed. Made in Brooklyn. © {new Date().getFullYear()}.
          </div>
          <div className="pb-footer-build">
            <BuildInfo />
          </div>
        </div>
        <div className="pb-footer-links">
          {FOOTER_LINKS.map((l) => (
            <a key={l} href="#" onClick={(e) => e.preventDefault()}>
              {l}
            </a>
          ))}
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
